import { useMemo } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "shared/components";
import {
  TreePropertyType,
  type MeasurementTypeTreePropertyDescriptor,
  type TreeDescriptor,
  TrunkTypeTreePropertyDescriptor,
  RemoveOrPruneTreePropertyDescriptor,
  SizeEstimateTreePropertyDescriptor,
} from "@/components/treeModule/lib";

import { TreeItemProperty } from "./TreeItemProperty";

export interface TreeItemPropertyListProps {
  treeDescriptor: TreeDescriptor;
}

export function TreeItemPropertyList({
  treeDescriptor,
}: TreeItemPropertyListProps) {
  const filteredProperties = useMemo(() => {
    const measurementType = treeDescriptor.properties.get(
      TreePropertyType.measurement_type,
    ) as MeasurementTypeTreePropertyDescriptor | undefined;

    const size = treeDescriptor.properties.get(TreePropertyType.size) as
      | SizeEstimateTreePropertyDescriptor
      | undefined;

    const trunkType = treeDescriptor.properties.get(
      TreePropertyType.trunk_type,
    ) as TrunkTypeTreePropertyDescriptor | undefined;

    const removeOrPrune = treeDescriptor.properties.get(
      TreePropertyType.remove_or_prune,
    ) as RemoveOrPruneTreePropertyDescriptor | undefined;

    return Array.from(treeDescriptor.properties.values()).filter((property) => {
      if (property.propertyType == TreePropertyType.measurement_type) {
        return false;
      }

      // if measurement type is detailed, don't show size
      if (
        measurementType?.value === "detailed" &&
        property.propertyType === TreePropertyType.size
      ) {
        return false;
      }

      // if measurement type is estimate, don't show dbh, circumference and height
      if (
        measurementType?.value === "estimate" &&
        (property.propertyType === TreePropertyType.dbh ||
          property.propertyType === TreePropertyType.circumference ||
          property.propertyType === TreePropertyType.height)
      ) {
        return false;
      }

      // if trunk type is vegetation, don't show dbh, circumference and height
      if (
        trunkType?.value === "vegetation" &&
        (property.propertyType === TreePropertyType.dbh ||
          property.propertyType === TreePropertyType.height ||
          property.propertyType === TreePropertyType.circumference)
      ) {
        return false;
      }

      // if remove or prune is empty, don't show reason
      if (
        property.propertyType === TreePropertyType.reason &&
        (!removeOrPrune || !removeOrPrune?.value)
      ) {
        return false;
      }

      // if remove or prune is remove, don't show pruning quantity
      if (
        property.propertyType === TreePropertyType.pruning_quantity &&
        (!removeOrPrune ||
          !removeOrPrune?.value ||
          removeOrPrune.value === "remove")
      ) {
        return false;
      }

      // if measurement type is estimate, and size is not medium or large,
      // don't show property_boundary
      if (property.propertyType === TreePropertyType.property_boundary) {
        if (
          measurementType?.value === "estimate" &&
          size?.value !== "medium" &&
          size?.value !== "large"
        ) {
          return false;
        }
      }

      // if measurement type is detailed, and trunk type is not single or multi,
      // don't show property_boundary
      if (property.propertyType === TreePropertyType.property_boundary) {
        if (
          measurementType?.value === "detailed" &&
          trunkType?.value !== "single" &&
          trunkType?.value !== "multi"
        ) {
          return false;
        }
      }

      // if measurement type is estimate, don't show canopy_size
      if (
        measurementType?.value === "estimate" &&
        property.propertyType === TreePropertyType.canopy_size
      ) {
        return false;
      }

      // if measurement type is detailed, and trunk type is not single or multi,
      // don't show canopy_size
      if (
        measurementType?.value === "detailed" &&
        trunkType?.value !== "single" &&
        trunkType?.value !== "multi" &&
        property.propertyType === TreePropertyType.canopy_size
      ) {
        return false;
      }

      return true;
    });
  }, [treeDescriptor]);

  return (
    <TooltipProvider>
      {filteredProperties.map((property) => (
        <Tooltip
          disableHoverableContent={!property.tooltip}
          key={property.propertyType}
        >
          <TooltipTrigger>
            <TreeItemProperty propertyDescriptor={property} />
          </TooltipTrigger>

          <TooltipContent side="bottom">{property.tooltip}</TooltipContent>
        </Tooltip>
      ))}
    </TooltipProvider>
  );
}
